// src/pages/FactDetailPage.tsx
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  IconButton,
  Button,
  Breadcrumbs,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Bookmark,
  BookmarkBorder,
  Share,
  ArrowBack,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../store';
import { Fact } from '../types';
import { likeFact } from '../store/slices/factsSlice';
import { triviaApi } from '../services/api';
import FactList from '../components/facts/FactList';

const FactDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  const [fact, setFact] = useState<Fact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedFacts, setRelatedFacts] = useState<Fact[]>([]);

  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);

  // 獲取單個冷知識詳情
  useEffect(() => {
    const fetchFactDetail = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const factData = await triviaApi.getTriviaById(id);
        setFact(factData);

        // 設置狀態
        setLiked(factData.likedBy.includes(userId || ''));

        // 獲取相關冷知識 (相同類別的其他冷知識)
        const randomFacts = await triviaApi.getRandomTriviaList(3, userId);
        setRelatedFacts(randomFacts.filter((f) => f.id !== id));

        setLoading(false);
      } catch (err) {
        console.error('獲取冷知識詳情失敗:', err);
        setError('無法獲取冷知識詳情');
        setLoading(false);
      }
    };

    fetchFactDetail();
  }, [id, userId]);

  // 類別對應顏色
  const getCategoryColor = (category: string): string => {
    const customColors: Record<string, string> = {
      nature: '#2e7d32', // 深綠色
      human: '#1976d2', // 藍色
      history: '#9c27b0', // 紫色
      science: '#0288d1', // 淺藍色
      tech: '#0097a7', // 青色
      food: '#d32f2f', // 紅色
      travel: '#ed6c02', // 橙色
      architecture: '#7b1fa2', // 深紫色
      law: '#c2185b', // 洋紅色
      probability: '#1565c0', // 深藍色
      others: '#757575', // 灰色
    };

    return customColors[category] || '#757575';
  };

  // 類別對應中文名稱
  const categoryNames: Record<string, string> = {
    nature: '自然與生物',
    human: '人體與心理',
    history: '歷史與文化',
    science: '科學與宇宙',
    tech: '科技與網路',
    food: '食物與日常生活',
    travel: '世界奇觀與旅遊',
    architecture: '建築與城市',
    law: '法律與規則',
    probability: '機率與巧合',
    others: '其他',
  };

  // 處理點讚
  const handleLike = () => {
    if (!isAuthenticated || !fact) {
      navigate('/auth');
      return;
    }

    dispatch(likeFact(fact.id));
    setLiked(!liked);
  };

  // 處理收藏
  const handleSave = () => {
    if (!isAuthenticated || !fact) {
      navigate('/auth');
      return;
    }

    setSaved(!saved);
    // 實際專案中這裡會處理收藏邏輯
  };

  // 處理分享
  const handleShare = () => {
    if (!fact) return;

    const url = window.location.href;

    if (navigator.share) {
      navigator
        .share({
          title: fact.title,
          text: fact.content.substring(0, 100) + '...',
          url: url,
        })
        .catch((err) => {
          console.error('分享失敗:', err);
          copyToClipboard(url);
        });
    } else {
      copyToClipboard(url);
    }
  };

  // 複製到剪貼簿
  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert('連結已複製到剪貼簿！');
      })
      .catch((err) => {
        console.error('無法複製連結:', err);
      });
  };

  // // 格式化日期
  // const formatDate = (dateString: string) => {
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString('zh-TW', {
  //     year: 'numeric',
  //     month: 'long',
  //     day: 'numeric',
  //   });
  // };

  // 載入中
  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // 錯誤處理
  if (error || !fact) {
    return (
      <Container sx={{ py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error || '未找到冷知識'}
          </Typography>
          <Button
            variant="contained"
            component={Link}
            to="/"
            startIcon={<ArrowBack />}
          >
            返回首頁
          </Button>
        </Paper>
      </Container>
    );
  }

  // 獲取顯示的類別名稱
  const displayCategoryName = categoryNames[fact.category] || fact.category;

  // 獲取類別背景顏色
  const categoryBgColor = getCategoryColor(fact.category);

  return (
    <Container sx={{ py: 4 }}>
      {/* 麵包屑導航 */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link to="/" className="text-gray-600 hover:text-gray-900 no-underline">
          首頁
        </Link>
        <Link
          to={`/categories/${fact.category}`}
          className="text-gray-600 hover:text-gray-900 no-underline"
        >
          {displayCategoryName}
        </Link>
        <Typography color="text.primary">{fact.title}</Typography>
      </Breadcrumbs>

      {/* 返回按鈕 */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        返回
      </Button>

      {/* 冷知識卡片 */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        {/* 標題和類別 */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            {fact.title}
          </Typography>

          <Chip
            label={displayCategoryName}
            sx={{
              bgcolor: categoryBgColor,
              color: 'white',
              fontWeight: 500,
            }}
          />
        </Box>

        {/* 內容 */}
        <Typography
          variant="body1"
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}
        >
          {fact.content}
        </Typography>

        {/* 來源 */}
        {fact.source && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              資料來源:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fact.source}
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          {/* <Typography variant="caption" color="text.secondary">
            發布日期: {formatDate(fact.date)}
          </Typography> */}
          {fact.createdBy && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              來源: AI 產生
            </Typography>
          )}
        </Box>
        <Divider sx={{ my: 3 }} />

        {/* 互動按鈕 */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Tooltip title={liked ? '取消讚' : '讚'}>
              <IconButton
                color={liked ? 'secondary' : 'default'}
                onClick={handleLike}
              >
                {liked ? <Favorite /> : <FavoriteBorder />}
              </IconButton>
            </Tooltip>

            <Typography variant="body2" sx={{ mr: 3 }}>
              {fact.likes} 個讚
            </Typography>

            <Tooltip title={saved ? '取消收藏' : '收藏'}>
              <IconButton
                color={saved ? 'primary' : 'default'}
                onClick={handleSave}
              >
                {saved ? <Bookmark /> : <BookmarkBorder />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box>
            <Tooltip title="分享">
              <IconButton onClick={handleShare}>
                <Share />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 日期信息 */}
      </Paper>

      {/* 相關冷知識 */}
      {relatedFacts.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            你可能也會喜歡
          </Typography>
          <FactList facts={relatedFacts} />
        </Box>
      )}
    </Container>
  );
};

export default FactDetailPage;
